import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { EnhancedButtonStatus } from '@components/button/EnhancedButton';
import ToastErrorMessage from '@components/toast/ToastErrorMessage';
import { drawerSections } from './content';
import { useLazyQuery, useMutation } from '@apollo/client';
import { cloneDeep, isEmpty } from 'lodash';
import { DEFAULT_ERROR_TEXT } from '@utils';
import { ILineCreditLimitDrawerInfo, ILineCreditLimitDrawerProps } from '.';
import { getAssignedLineInfo, updateLineCreditLimit } from './queries';
import { graphqlToDefaultLineCreditLimitInfo } from './utils';
import { getError } from '@utils/graph-utils';
import ToastSuccessMessage from '@components/toast/ToastSuccessMessage';
import DynamicFormDrawer from '@components/dynamic-form/DynamicFormDrawer';
import { IDynamicFormWidgetSection } from '@components/dynamic-form/types';
import { ISelectFormFieldProps } from '@components/dynamic-form/types/inputTypes';
import { normalizeDynamicFormDrawerValues } from '@components/dynamic-form/utils';

const LineCreditLimitDrawer: React.FC<ILineCreditLimitDrawerProps> = ({
  open,
  onSuccess,
  onClose,
  businessPartnerId,
  businessPartnerLinesId,
}) => {
  const [booted, setBooted] = useState<boolean>(false);
  const [formDisabled, setFormDisabled] = useState(false);
  const [submitButtonState, setSubmitButtonState] =
    useState<EnhancedButtonStatus>();
  const [inputsForm, setInputsForm] =
    useState<IDynamicFormWidgetSection[]>(drawerSections);

  const [defaultLineCreditLimitInfoQuery] = useLazyQuery(getAssignedLineInfo);
  const [lineCreditLimitAction] = useMutation(updateLineCreditLimit());

  const getEntityInfo = async () => {
    let defaultLineCreditLimit: ILineCreditLimitDrawerInfo = {
      creditLimit: null,
      creditLimitCurrency: '39735',
      gracePeriod: null,
    };

    if (businessPartnerLinesId && businessPartnerLinesId.length === 1) {
      const apiResult = await defaultLineCreditLimitInfoQuery({
        variables: { id: businessPartnerLinesId[0] },
      });
      if (apiResult.data) {
        defaultLineCreditLimit = graphqlToDefaultLineCreditLimitInfo(
          apiResult?.data
        );
      }
    }

    return defaultLineCreditLimit;
  };

  useEffect(() => {
    initialize();
  }, []);

  const initialize = async () => {
    try {
      const updatedSections = cloneDeep(inputsForm);
      const updatedInputs = updatedSections[0].inputs;

      const entityData = await getEntityInfo();

      if (entityData) {
        updatedInputs.creditLimitCurrency.value =
          entityData.creditLimitCurrency || '39735';
        updatedInputs.creditLimit.value = entityData.creditLimit;
        updatedInputs.gracePeriod.value = entityData.gracePeriod;
        setInputsForm(updatedSections);
      }
      setBooted(true);
    } catch (err) {
      toast.error(<ToastErrorMessage>{DEFAULT_ERROR_TEXT}</ToastErrorMessage>);
    }
  };

  const submitForm = async (values: Record<string, any>) => {
    const [data] = normalizeDynamicFormDrawerValues(drawerSections, values);

    setFormDisabled(true);
    setSubmitButtonState('loading');

    try {
      const variablesMutation = {
        entityId: businessPartnerId,
        selectedBusinessPartnerLineIds: businessPartnerLinesId,
        creditLimit: parseInt(data.creditLimit as unknown as string),
        creditLimitCurrency: data.creditLimitCurrency || null,
        gracePeriod: parseInt(data.gracePeriod as unknown as string),
      };

      const res = await lineCreditLimitAction({
        variables: variablesMutation,
        errorPolicy: 'all',
      });

      if (isEmpty(res.errors)) {
        toast.success(
          <ToastSuccessMessage>
            Credit Limit successfully updated.
          </ToastSuccessMessage>
        );

        setTimeout(() => {
          setFormDisabled(false);
          setSubmitButtonState('success');
          onSuccess();
          onClose();
        }, 500);
      } else {
        setSubmitButtonState(undefined);
        setFormDisabled(false);
        toast.error(<ToastErrorMessage>{getError(res)}</ToastErrorMessage>);
      }
    } catch (err) {
      setFormDisabled(false);
      setSubmitButtonState(undefined);
      toast.error(<ToastErrorMessage>{getError(err)}</ToastErrorMessage>);
    }
  };

  return (
    <DynamicFormDrawer
      title={'Modify Line Credit Limit'}
      onClose={() => onClose()}
      isOpen={open}
      loading={!booted}
      sections={inputsForm}
      onSubmit={(values) => submitForm(values)}
      submitButtonState={submitButtonState}
      disableForm={formDisabled}
    />
  );
};

export default LineCreditLimitDrawer;
